import { apiAuth } from '../config/api';



async function getOffers() {
    return await apiAuth.fetch(`/api/offer/dealer`, {
        method: 'GET',
    });
}

/**
 * Transforms the original offer data into a collapsible structure
 * where each item represents a completed offer with its negotiation timeline
 */
export const transformOffersToCollapsible = (originalOffers) => {
    // Group offers by their latest createdAt offer in the chain
    const offerGroups = new Map();

    originalOffers.forEach(offer => {
        // Use offer.id instead of offer._id for the current API response
        const offerId = offer.id || offer._id;
        
        // Find the offer with the latest createdAt in this chain
        let latestOffer = offer;
        let currentOffer = offer;

        // Traverse through children to find the latest createdAt offer
        while (currentOffer.children && currentOffer.children.length > 0) {
            const latestChild = currentOffer.children.reduce((latest, child) => {
                return new Date(child.created_at || child.createdAt) > new Date(latest.created_at || latest.createdAt) ? child : latest;
            }, currentOffer);
            if (latestChild !== currentOffer) {
                latestOffer = latestChild;
                currentOffer = latestChild;
            } else {
                break;
            }
        }

        // If this offer is already a child of another, skip it
        if (offer.originalOffer) {
            return;
        }

        // Create timeline for this offer chain
        const timeline = [];

        // Add all offers in the chain to timeline
        const addOfferToTimeline = (currentOffer, isLatest = false) => {
            timeline.push({
                id: currentOffer.id || currentOffer._id,
                type: isLatest ? 'latest' : (currentOffer.status === 'accepted' ? 'final' : 'countered'),
                status: currentOffer.status,
                amount: currentOffer.amount,
                createdAt: currentOffer.created_at || currentOffer.createdAt,
                updatedAt: currentOffer.updated_at || currentOffer.updatedAt,
                appointmentId: currentOffer.appointment
            });
        };

        // Add the initial offer if it's not the latest
        const latestOfferId = latestOffer.id || latestOffer._id;
        if (offerId !== latestOfferId) {
            addOfferToTimeline(offer);
        }

        // Add counter offers if they exist
        if (offer.children && offer.children.length > 0) {
            offer.children.forEach(child => {
                const childId = child.id || child._id;
                if (childId !== latestOfferId) {
                    addOfferToTimeline(child);
                }
            });
        }

        // Add the latest offer
        addOfferToTimeline(latestOffer, true);

        // Sort timeline by creation date (newest first)
        timeline.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        // Create the collapsible item with proper user structure
        const collapsibleItem = {
            id: latestOfferId,
            isParent: true,
            status: latestOffer.status,
            amount: latestOffer.amount,
            user: {
                ...latestOffer.user,
                userInfo: {
                    name: latestOffer.user?.name,
                    email: latestOffer.user?.email,
                    phone: latestOffer.user?.phone
                }
            },
            createdAt: latestOffer.created_at || latestOffer.createdAt,
            vehicle: {
                askingPrice: latestOffer.vehicle?.price || latestOffer.vehicle?.askingPrice,
                make: latestOffer.vehicle?.make,
                model: latestOffer.vehicle?.model,
                year: latestOffer.vehicle?.year,
                mileage: latestOffer.vehicle?.mileage,
                color: latestOffer.vehicle?.externalColor || latestOffer.vehicle?.color,
                image: latestOffer.vehicle?.images?.[0]?.url || '',
                stockId: latestOffer.vehicle?.stockId,
                vin: latestOffer.vehicle?.vin,
                trim: latestOffer.vehicle?.trim,
                engine: latestOffer.vehicle?.engine,
                transmission: latestOffer.vehicle?.transmission,
                driveTrain: latestOffer.vehicle?.driveTrain,
                exteriorColor: latestOffer.vehicle?.exteriorColor,
                interiorColor: latestOffer.vehicle?.interiorColor,
                isUsed: latestOffer.vehicle?.isUsed,
                isCertified: latestOffer.vehicle?.isCertified,
                images: latestOffer.vehicle?.images || [],
                msrp: latestOffer.vehicle?.msrp,
            },
            timeline: timeline
        };

        offerGroups.set(latestOfferId, collapsibleItem);
    });

    // Convert to array and sort by creation date (newest first)
    return Array.from(offerGroups.values())
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};


// Main function: returns array for Accordion
export async function getOffersAccordionData() {
    const response = await getOffers();
    const offersData = response?.data || [];
    return {
        data: transformOffersToCollapsible(offersData),
        status: response?.status,
        message: response?.message,
    }
}
